<template>
  <!--
    Stays put. It carries the account, and inside a venue the table you are
    sitting at — both of which are wanted at the bottom of a 278-dish menu at
    least as much as at the top, and neither is reachable from anywhere else.

    It therefore needs a ground of its own: sticky chrome with no background is
    chrome the menu scrolls straight through, which is exactly what a missing
    fill looked like the last time it happened here.

    h-16 is load-bearing rather than arbitrary — PjCategoryTabs pins at top-16,
    directly beneath this. The two have to agree, and there is no third place
    that needs to know, so the pair of Tailwind classes is the whole contract.
    Nothing measures anything.
  -->
  <!-- The rule belongs to whichever element closes the pinned block. Where a
       category strip is directly beneath (Menu, with items) it carries the
       line instead — two hairlines a row apart read as a box drawn round the
       chrome. Every other page, venue or not, has nothing else to carry it. -->
  <header :class="['sticky top-0 z-bar w-full bg-bg', !props.hasCategoryStrip && 'border-b']">
    <div
      class="pj-container flex h-16 items-center justify-between gap-4"
      :style="containerStyle"
    >
      <div class="flex min-w-0 items-center gap-3">
        <!--
          Home, and the brand, as one object — except inside a venue, where it is
          inert. A wordmark that is not a link is a dead end on the directory,
          and a wordmark that IS one is a route out of a restaurant to a list of
          its competitors. That route is the half of the silence rule still
          standing, so the mark loses its href rather than the venue losing the
          mark. See ARCH-2026-011.

          Shown at every width, venue or not — but not at the same width. A phone
          gets the mark alone; the wordmark is 152pt of brand on a row that also
          has to hold a venue's name, its table and an account, and the mark on
          its own is the half of the lockup a diner needs least often. Both are in
          the DOM and CSS picks one, as with the ground above.
        -->
        <Link
          aria-label="Places"
          class="flex shrink-0 items-center gap-1.5 pj-tap hover:opacity-80"
          :href="placesHref"
        >
          <PjMark
            class="h-6 w-auto sm:hidden"
            variant="mark"
          />
          <PjMark
            class="hidden h-6 w-auto sm:block lg:h-7"
            variant="logo"
          />
          <span
            class="inline-flex items-center rounded-xs border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary"
          >
            Beta
          </span>
        </Link>

        <!-- Whose menu you are looking at, beside whose app you are in. -->
        <template v-if="isVenue && props.venueName">
          <span
            aria-hidden="true"
            class="h-6 w-px shrink-0 bg-line"
          />

          <Link
            class="flex min-w-0 items-center gap-2 pj-tap hover:opacity-80"
            :href="venueHref"
          >
            <span
              class="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-logo"
              :style="{ backgroundColor: `var(--pj-venue-${props.venueAccent ?? 'navy'})` }"
            >
              <img
                v-if="props.venueLogoUrl && !venueLogoFailed"
                alt=""
                class="h-full w-full object-cover"
                :src="props.venueLogoUrl"
                @error="venueLogoFailed = true"
              />
              <span
                v-else
                class="text-caption text-white"
              >
                {{ venueMonogram }}
              </span>
            </span>

            <span class="min-w-0 truncate text-title font-semibold text-ink">{{ props.venueName }}</span>
          </Link>
        </template>
      </div>

      <!-- Where a page may put its search, beside the brand, from md up. Places
           teleports its field here; a page with no search leaves it empty. Never
           on a venue's page, which searches its own menu in place. -->
      <div
        v-if="!isVenue"
        :id="PJ_HEADER_SEARCH_ID"
        class="hidden min-w-0 max-w-md flex-1 md:flex"
      />

      <!--
        One control, and the same one at every width.

        This replaces a bottom bar carrying Places and You. Two destinations did
        not earn a permanent band across the bottom of a phone: the wordmark
        beside it already goes to Places, which left one real item sitting as far
        from the content as the screen allows. The desktop bar it also replaces
        had a different shape again — a destination and a disclosure — so the two
        breakpoints disagreed about what the app's own navigation was.

        A disclosure rather than a link to the account screen, in both states:
        appearance belongs to the device rather than to whoever is signed in, and
        putting it behind a screen you must sign in to reach buried it under two
        things it has nothing to do with.
      -->
      <div class="flex shrink-0 items-center gap-2">
        <!-- What this order is, and the way to change it. It was the table's one
             home; it is now the mode's too, because at a venue that does both
             those are the same question — which is why one control answers it.
             On screen at every scroll position, which is what "am I dining in,
             and where" actually needs. -->
        <PjOrderMode
          v-if="isVenue"
          compact
          :context="props.orderContext ?? null"
          @choose-mode="emit('chooseMode', $event)"
          @manage-table="emit('manageTable')"
          @pick-table="emit('pickTable')"
          @scan="emit('scan')"
        />

        <div class="relative">
          <button
            ref="triggerEl"
            :aria-controls="MENU_ID"
            :aria-expanded="isMenuOpen"
            :aria-label="accountLabel"
            class="-mr-1 flex min-h-tap items-center gap-2 rounded-full px-1 text-title pj-tap max-sm:mr-0 max-sm:h-9 max-sm:min-h-9 max-sm:w-9 max-sm:justify-center max-sm:bg-container max-sm:px-0"
            :class="isMenuOpen ? 'text-ink' : 'text-muted hover:text-ink'"
            type="button"
            @click="isMenuOpen = !isMenuOpen"
          >
            <span class="relative flex">
              <!-- The face once there is one, the glyph until then. Sized to the
                 xs button beside it — see PjAvatar — so the row reads as two
                 controls of one size rather than a pill and a detail. The 64px
                 row is taller than either, so neither can change its height. -->
              <PjAvatar
                v-if="auth.isSignedIn"
                :initials="auth.initials"
                size="nav"
                :src="auth.avatarUrl"
              />
              <PjIcon
                v-else
                :name="PJ_ICON.YOU"
                size="nav"
              />
              <!-- Live orders, and gone at zero. A badge that is always there
                 stops being a signal. -->
              <span
                v-if="auth.isSignedIn && liveOrderCount > 0"
                class="pj-num absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] text-on-primary"
              >
                {{ liveOrderCount }}
              </span>
            </span>

            <!-- Signed out it says the thing you would actually do. "You" is a
               place, and naming a place nobody can enter yet describes the app's
               structure rather than the diner's next move.

               Gone on a phone, along with the chevron: the avatar is the whole
               control there, and the name it repeats is the least useful text on
               a row already holding the venue's. The button keeps the label as
               its aria-label, so what is dropped is pixels and not the name. -->
            <span
              class="hidden sm:inline"
              :class="auth.isSignedIn ? 'max-w-[8rem] truncate text-ink' : undefined"
              >{{ accountLabel }}</span
            >

            <PjIcon
              class="!hidden transition-transform duration-base motion-reduce:transition-none sm:!block"
              :class="isMenuOpen && 'rotate-180'"
              :name="PJ_ICON.CHEVRON_DOWN"
              size="meta"
            />
          </button>

          <Transition
            enter-active-class="duration-base ease-pj"
            enter-from-class="scale-95 opacity-0 motion-reduce:scale-100"
            leave-active-class="duration-exit ease-pj"
            leave-to-class="scale-95 opacity-0 motion-reduce:scale-100"
          >
            <PjAccountMenu
              v-if="isMenuOpen"
              :id="MENU_ID"
              ref="menuEl"
              class="absolute right-0 top-full z-bar mt-2 origin-top-right"
              :current="isAccountActive"
              :live-orders="liveOrderCount"
              :stay-put="isVenue"
              @close="isMenuOpen = false"
              @open-account="emit('openAccount')"
            />
          </Transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue'

import { Link, usePage } from '@inertiajs/vue3'
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { computed, ref, useTemplateRef, watch } from 'vue'

import type { PjContainerWidth } from '@/domains/pjazza/composables/use-container-width/useContainerWidth.ts'
import type { OrderContext } from '@/domains/pjazza/composables/use-order-context/useOrderContext.ts'
import type { PjChromeContext } from '@/domains/pjazza/types/consts/AppContext.consts.ts'
import type { OrderMode } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'
import type { VenueAccent } from '@/domains/shared/types/consts/pjazza/VenueAccent.consts.ts'

import PjAvatar from '@/domains/pjazza/components/atoms/pj-avatar/PjAvatar.vue'
import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import PjMark from '@/domains/pjazza/components/atoms/pj-mark/PjMark.vue'
import PjAccountMenu from '@/domains/pjazza/components/molecules/pj-account-menu/PjAccountMenu.vue'
import PjOrderMode from '@/domains/pjazza/components/molecules/pj-order-mode/PjOrderMode.vue'
import { useContainerWidth } from '@/domains/pjazza/composables/use-container-width/useContainerWidth.ts'
import { usePjazzaAuthStore } from '@/domains/pjazza/store/use-pjazza-auth-store/usePjazzaAuthStore.ts'
import { PJ_APP_CONTEXT, PJ_HEADER_SEARCH_ID } from '@/domains/pjazza/types/consts/AppContext.consts.ts'

/**
 * The app's only chrome, on every screen that has any: the brand, where you are,
 * and who you are.
 *
 * There is no bar above or below it at any width. Two destinations did not
 * justify one, and having a bottom bar on a phone and a top bar on a desktop
 * meant the app's own navigation changed shape at 1024px for no reason a diner
 * could see. It is in flow rather than fixed, so it scrolls away and gives the
 * whole window back to the menu.
 *
 * Venue pages use it too, which reverses half of the old silence rule and keeps
 * the half that pays for itself. What a venue's page still has is no route to
 * another venue — the wordmark is inert there, and there is no Places. What it
 * gains is a diner able to reach their own account and their own orders without
 * leaving the menu they are ordering from. See ARCH-2026-011.
 */
const props = defineProps<{
  liveOrders?: number
  /** Must match the page container, or the lockup sits off-column. */
  width?: PjContainerWidth
  /**
   * 'venue' names the venue beside the wordmark, offers the table you are
   * sitting at instead of a route home, and makes the account panel open a sheet
   * in place — leaving a venue mid-order would take the basket and table with it.
   */
  context?: PjChromeContext
  siteSlug?: string | null
  venueName?: string | null
  venueLogoUrl?: string | null
  venueAccent?: VenueAccent
  /**
   * What the order is and what can be done about it, resolved once by
   * `useOrderContext`. Six props used to say this between them, which is how the
   * shell and the menu page came to disagree about a table.
   */
  orderContext?: OrderContext | null
  /** True while the page below has its own category strip glued to this header. */
  hasCategoryStrip?: boolean
}>()

const emit = defineEmits<{
  pickTable: []
  manageTable: []
  openAccount: []
  chooseMode: [mode: OrderMode]
  scan: []
}>()

const isVenue = computed(() => props.context === PJ_APP_CONTEXT.VENUE)

const venueLogoFailed = ref(false)

// Two letters, from two words where there are two — "Ta' Rikardu" reads as TR,
// not TA.
const venueMonogram = computed(() => {
  const words = (props.venueName ?? '').split(/\s+/).filter(Boolean)

  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : (props.venueName ?? '').slice(0, 2)).toUpperCase()
})

const auth = usePjazzaAuthStore()

const page = usePage<{ ziggy?: { location?: string } }>()

const containerStyle = useContainerWidth(() => props.width ?? 'wide')

const MENU_ID = 'pj-account-menu'

const isMenuOpen = ref(false)

/*
 * Nobody's orders when nobody is signed in.
 *
 * The count is persisted so the badge can render without a fetch, and an
 * explicit sign-out clears it — but a token expiring clears nothing, because
 * nothing runs when it happens. Gated here rather than in the store: the count
 * is a diner's across every venue, so the store is right to hold it plainly,
 * and this is the only place it is ever drawn.
 */
const liveOrderCount = computed(() => (auth.isSignedIn ? (props.liveOrders ?? 0) : 0))

const placesHref = computed(() => route('pjazza.places'))

const venueHref = computed(() => (props.siteSlug ? route('pjazza.menu', { site: props.siteSlug }) : '#'))

const path = computed(() => {
  const url = page.props.ziggy?.location ?? ''

  return url ? new URL(url).pathname : window.location.pathname
})

const isAccountActive = computed(() => /^\/you/.test(path.value))

// Their own first name once we have it — the same label the avatar beside it is
// drawn from.
const accountLabel = computed(() => {
  if (!auth.isSignedIn) return 'Sign in'

  return auth.name?.trim().split(/\s+/)[0] || 'You'
})

const trigger = useTemplateRef<HTMLButtonElement>('triggerEl')
const menu = useTemplateRef<ComponentPublicInstance>('menuEl')

// The trigger is excluded, or its own click closes and reopens in one gesture.
onClickOutside(
  menu,
  () => {
    isMenuOpen.value = false
  },
  { ignore: [trigger] }
)

// Escape is the one dismissal that hands focus back: a click has already put it
// somewhere the diner chose, and pulling it away from there is worse.
onKeyStroke('Escape', () => {
  if (!isMenuOpen.value) return

  isMenuOpen.value = false
  trigger.value?.focus()
})

// Any navigation closes it, not just the links inside — a browser back out of the
// account screen would otherwise leave the panel over a page it no longer belongs
// to.
watch(path, () => {
  isMenuOpen.value = false
})
</script>
